import React, { useEffect, useState } from 'react';
import { View, ScrollView, Pressable, StyleSheet, Alert, ActivityIndicator, Switch } from 'react-native';
import { Text, TextInput } from '../ui/Text';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useAuth } from '../auth';
import {
  useRoom, useRooms, useIsAgent, loadMembers, inviteUser, inviteByEmail, leaveRoom, addRoomToSpace, removeRoomFromSpace, fetchSpaceRooms, joinRoom,
  canSetState, canInvite, roomAdmin, senderName, setRoomName, Room, SpaceRoom, canKick, adminsOnlyInvite, setAdminsOnlyInvite, removeFromWorkspace
} from '../matrix';
import { usePrefs } from '../prefs';
import { Btn } from '../ui/Btn';
import { UserPicker } from '../ui/UserPicker';
import { KeyboardShift } from '../ui/KeyboardShift';
import { Avatar, RoomAvatar } from '../ui/Avatar';
import { RenameTitle } from '../ui/RenameTitle';
import { radius, makeStyles, useTheme, webSwitch } from '../theme';
import type { RootStackParamList } from '../navTypes';

type Props = NativeStackScreenProps<RootStackParamList, 'Workspace'>;

/** A Matrix Space = a workspace. Admins manage which rooms are in it and who's invited; everyone else sees it read-only. */
export default function WorkspaceScreen({ route, navigation }: Props) {
  const theme = useTheme(); const s = useStyles();
  const { spaceId } = route.params;
  const { userId } = useAuth();
  const space = useRoom(spaceId);
  const all = useRooms();
  const isAgent = useIsAgent();
  const { humanBadge } = usePrefs();
  const candidates = all.filter((r) => !r.isSpace).sort((a, b) => a.name.localeCompare(b.name));
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [available, setAvailable] = useState<SpaceRoom[]>([]);

  useEffect(() => { navigation.setOptions({ title: space?.name || 'Workspace' }); }, [space?.name]);
  useEffect(() => { if (space && !space.membersLoaded) loadMembers(spaceId).catch(() => {}); }, [spaceId, space?.membersLoaded]);
  // Rooms in this workspace I haven't joined yet (visible because workspace members may join them).
  const refreshAvailable = () => fetchSpaceRooms(spaceId).then((rs) => setAvailable(rs.filter((r) => !r.joined))).catch(() => {});
  useEffect(() => { refreshAvailable(); }, [spaceId, space?.children.length]);

  if (!space) return <View style={s.center}><ActivityIndicator color={theme.accent} /></View>;

  const run = async (key: string, fn: () => Promise<any>, ok?: string) => {
    setBusy(key); setNote(null);
    try { await fn(); if (ok) setNote(ok); }
    catch (e: any) { Alert.alert('That didn’t work', e?.message || 'Unknown error'); }
    finally { setBusy(null); }
  };
  const toggle = (roomId: string, on: boolean) =>
    run(roomId, () => (on ? addRoomToSpace(spaceId, roomId) : removeRoomFromSpace(spaceId, roomId)),
      on ? 'Added — workspace members can now join this room' : 'Removed — room is invite-only again');
  const join = (r: SpaceRoom) => run(`join:${r.id}`, async () => { await joinRoom(r.id); setAvailable((cur) => cur.filter((x) => x.id !== r.id)); }, `Joined ${r.name}`);
  const leave = () => Alert.alert('Leave workspace?', `The rooms inside stay; you just leave "${space.name}".`, [
    { text: 'Cancel', style: 'cancel' },
    { text: 'Leave', style: 'destructive', onPress: () => run('leave', async () => { await leaveRoom(spaceId); navigation.popToTop(); }) },
  ]);
  const openRoom = (r: Room) => navigation.navigate('Chat', { roomId: r.id, title: r.name });

  const admin = canSetState(space, 'm.space.child');
  const mayInvite = canInvite(space);
  const adminsOnly = adminsOnlyInvite(space);
  const mayRemove = canKick(space);
  const remove = (uid: string, name: string) => Alert.alert(`Remove ${name}?`, `They leave "${space.name}" and every room in it that you manage. They can be invited again later.`, [
    { text: 'Cancel', style: 'cancel' },
    { text: 'Remove', style: 'destructive', onPress: () => run(`remove:${uid}`, async () => {
      const r = await removeFromWorkspace(spaceId, uid);
      setNote(`Removed ${name} from the workspace${r.removed.length ? ` and ${r.removed.length} room${r.removed.length === 1 ? '' : 's'}` : ''}.`
        + (r.kept.length ? ` Still in ${r.kept.join(', ')}: you don’t manage ${r.kept.length === 1 ? 'that room' : 'those rooms'}.` : ''));
    }) },
  ]);
  const adminId = roomAdmin(space);
  const adminName = adminId ? (adminId === userId ? 'you' : senderName(space, adminId)) : 'the workspace admin';
  const members = Object.entries(space.members).sort(([a, an], [b, bn]) => (a === userId ? -1 : b === userId ? 1 : an.localeCompare(bn)));
  const inRooms = candidates.filter((r) => space.children.includes(r.id));

  return (
    <KeyboardShift>
    <ScrollView style={s.root} contentContainerStyle={{ padding: 16, paddingBottom: 40 }} keyboardShouldPersistTaps="handled">
      <View style={s.hero}>
        <Avatar name={space.name} mxc={space.avatarMxc} kind="space" size={56} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <RenameTitle name={space.name} canRename={canSetState(space, 'm.room.name')} onSave={(n) => setRoomName(space.id, n)} placeholder="Workspace name" />
          <Text style={s.meta}>Workspace · {inRooms.length} room{inRooms.length === 1 ? '' : 's'} · {members.length} member{members.length === 1 ? '' : 's'}</Text>
          <Text style={[s.meta, admin && { color: theme.good }]}>{admin ? 'You manage this workspace' : `Managed by ${adminName}`}</Text>
        </View>
      </View>
      {note ? <Text style={s.note}>{note}</Text> : null}

      {admin ? (
        <View style={s.card}>
          <Text style={s.h}>Rooms in this workspace</Text>
          <Text style={s.hint}>Flip a room on to put it in this workspace and let workspace members join it. Who may join is always decided per room — rooms you don't admin stay invite-only.</Text>
          {candidates.length === 0 && <Text style={s.hint}>You have no rooms yet.</Text>}
          {candidates.map((r) => {
            const on = space.children.includes(r.id);
            const shared = r.joinRule === 'restricted';
            const status = !on ? 'Not in this workspace'
              : shared ? '✓ Shared — workspace members can join'
                : '⚠ In workspace, but invite-only (you’re not this room’s admin)';
            return (
              <View key={r.id} style={s.row}>
                <RoomAvatar room={r} me={userId} isAgent={isAgent} size={32} humanBadge={humanBadge} />
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={s.name} numberOfLines={1}>{r.name}</Text>
                  <Text style={[s.id, on && shared && { color: theme.good }, on && !shared && { color: theme.warn }]} numberOfLines={1}>{status}</Text>
                </View>
                {busy === r.id ? <ActivityIndicator color={theme.accent} /> : (
                  <Switch {...webSwitch} value={on} onValueChange={(v) => toggle(r.id, v)} trackColor={{ true: theme.accent, false: theme.surface2 }} thumbColor="#fff" />
                )}
              </View>
            );
          })}
        </View>
      ) : (
        <View style={s.card}>
          <Text style={s.h}>Rooms in this workspace</Text>
          <Text style={s.hint}>Only {adminName} can add or remove rooms here. These are the ones you’re in.</Text>
          {inRooms.length === 0 && <Text style={s.hint}>You’re not in any of this workspace’s rooms yet.</Text>}
          {inRooms.map((r) => (
            <Pressable key={r.id} onPress={() => openRoom(r)} style={({ pressed }) => [s.row, pressed && { backgroundColor: theme.surface2 }]}>
              <RoomAvatar room={r} me={userId} isAgent={isAgent} size={32} humanBadge={humanBadge} />
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={s.name} numberOfLines={1}>{r.name}</Text>
                <Text style={s.id} numberOfLines={1}>{r.joinRule === 'restricted' ? 'Shared with the workspace' : 'Invite-only'}</Text>
              </View>
              <Text style={s.chev}>›</Text>
            </Pressable>
          ))}
        </View>
      )}

      {available.length > 0 && (
        <View style={s.card}>
          <Text style={s.h}>Rooms you can join</Text>
          <Text style={s.hint}>Shared with this workspace — you're not in them yet.</Text>
          {available.map((r) => (
            <View key={r.id} style={s.row}>
              <Avatar name={r.name} kind="room" size={32} />
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={s.name} numberOfLines={1}>{r.name}</Text>
                <Text style={s.id} numberOfLines={1}>{r.members} member{r.members === 1 ? '' : 's'}{r.topic ? ` · ${r.topic}` : ''}</Text>
              </View>
              <Btn small label="Join" onPress={() => join(r)} busy={busy === `join:${r.id}`} />
            </View>
          ))}
        </View>
      )}

      {mayInvite && (
        <View style={s.card}>
          <Text style={s.h}>Invite someone to the workspace</Text>
          <Text style={s.hint}>They'll be able to open every room shared with this workspace. Rooms marked invite-only stay private.</Text>
          <UserPicker exclude={Object.keys(space.members)} confirmLabel="Invite" busy={busy === 'invite'}
            onPick={(u) => run('invite', async () => { const mxid = await inviteUser(spaceId, u.id); setNote(`Invited ${u.name} (${mxid}) to the workspace`); })}
            onPickEmail={(email) => run('invite', async () => { const r = await inviteByEmail(spaceId, email); setNote(r.status === 'invited' ? `${email} already has an account and is now invited.` : `Invitation email sent to ${email}. They join the workspace once they sign up.`); })} />
        </View>
      )}

      {canSetState(space, 'm.room.power_levels') && (
        <View style={s.card}>
          <Text style={s.h}>Who can invite</Text>
          <View style={s.row}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={s.name}>Only admins can invite people</Text>
              <Text style={s.id}>{adminsOnly ? 'On: members ask an admin to bring someone in' : 'Off: anyone in the workspace can invite'}</Text>
            </View>
            {busy === 'policy' ? <ActivityIndicator color={theme.accent} /> : (
              <Switch {...webSwitch} value={adminsOnly} onValueChange={(v) => run('policy', () => setAdminsOnlyInvite(spaceId, v), v ? 'Only admins can invite now' : 'Anyone in the workspace can invite now')} trackColor={{ true: theme.accent, false: theme.surface2 }} thumbColor="#fff" />
            )}
          </View>
          <Text style={s.hint}>This also decides who may bring someone new into this workspace’s rooms. Members can always add people who are already in the workspace.</Text>
        </View>
      )}

      <View style={s.card}>
        <Text style={s.h}>Members</Text>
        {!mayInvite && <Text style={s.hint}>Ask {adminName} to invite more people.</Text>}
        {members.map(([uid, name]) => (
          <View key={uid} style={s.row}>
            <Avatar name={name} mxc={space.memberAvatars[uid]} kind={isAgent(uid) ? 'agent' : 'human'} size={32} humanBadge={humanBadge} me={uid === userId} />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={s.name} numberOfLines={1}>{name}{uid === userId ? ' (you)' : ''}</Text>
              <Text style={s.id} numberOfLines={1}>{uid}</Text>
            </View>
            {isAgent(uid) && <Text style={[s.tag, s.tagBot]}>Bot</Text>}
            {uid === adminId && <Text style={s.tag}>Admin</Text>}
            {mayRemove && uid !== userId && uid !== adminId && <Btn small kind="ghost" label="Remove" onPress={() => remove(uid, name)} busy={busy === `remove:${uid}`} />}
          </View>
        ))}
        {!space.membersLoaded && <ActivityIndicator color={theme.accent} style={{ marginTop: 8 }} />}
      </View>

      <View style={[s.card, { gap: 10 }]}>
        <Btn label="Leave workspace" kind="danger" onPress={leave} busy={busy === 'leave'} />
      </View>
      <Text style={s.idline} selectable>{space.id}</Text>
    </ScrollView>
    </KeyboardShift>
  );
}

const useStyles = makeStyles((theme) => StyleSheet.create({
  root: { flex: 1, backgroundColor: theme.bg },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.bg },
  hero: { flexDirection: 'row', alignItems: 'center', gap: 14 },
  title: { color: theme.ink, fontSize: 22, fontWeight: '800' },
  meta: { color: theme.faint, fontSize: 12, marginTop: 4, fontFamily: theme.mono },
  note: { color: theme.good, fontSize: 13, marginTop: 8, fontWeight: '600' },
  card: { backgroundColor: theme.surface, borderWidth: 1, borderColor: theme.border, borderRadius: radius.lg, padding: 14, marginTop: 14 },
  h: { color: theme.faint, fontSize: 11, fontWeight: '800', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6, fontFamily: theme.mono },
  hint: { color: theme.muted, fontSize: 13, marginBottom: 8, lineHeight: 18 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 7, borderRadius: radius.md },
  name: { color: theme.ink, fontSize: 15, fontWeight: '600' },
  id: { color: theme.faint, fontSize: 12 },
  chev: { color: theme.faint, fontSize: 20, paddingHorizontal: 6 },
  tag: { color: theme.muted, fontSize: 10.5, fontWeight: '800', letterSpacing: 0.6, textTransform: 'uppercase', backgroundColor: theme.surface2, borderRadius: radius.pill, paddingHorizontal: 8, paddingVertical: 3, overflow: 'hidden', fontFamily: theme.mono },
  tagBot: { color: theme.agentText, backgroundColor: theme.agentBg },
  input: { backgroundColor: theme.surface2, borderWidth: 1, borderColor: theme.border, borderRadius: radius.md, color: theme.ink, fontSize: 15, paddingHorizontal: 12, paddingVertical: 10 },
  idline: { color: theme.faint, fontSize: 11, marginTop: 16, textAlign: 'center', fontFamily: theme.mono },
}));
